import { cn } from "@/lib/utils";

import {
  SECCIONES_DE_ANTECEDENTES,
  avanceDeLaPestana,
  type ClaveDeSeccion,
  type Pendiente,
} from "./antecedentesGenerales";

/** Una fila del índice: a dónde lleva, cómo se rotula y si está completa. */
export interface EntradaDelIndice {
  /** El `id` del DOM al que lleva. Lo decide quien la dibuja: acá no hay DOM. */
  destino: string;
  /** "01", "02"… Vacío en las filas anidadas. */
  n: string;
  etiqueta: string;
  completa: boolean;
  /** Lo que va a la derecha de la fila ("2/3"). */
  cuenta?: string;
  /** 1 = fila anidada bajo la anterior, más chica y con sangría. */
  nivel?: 0 | 1;
}

export interface PendienteDelIndice {
  destino: string;
  texto: string;
}

interface IndiceLateralProps {
  /** Título de la tarjeta del índice. */
  titulo: string;
  /** Nombre accesible del `<nav>`. */
  etiquetaDeLaNavegacion: string;
  entradas: EntradaDelIndice[];
  avance: { hechas: number; total: number; porcentaje: number };
  pendientes: PendienteDelIndice[];
  onIr: (destino: string) => void;
}

/**
 * La columna fija de la derecha de una pestaña con tarjetas numeradas: el
 * índice con su avance y la lista de lo que falta.
 *
 * GENÉRICA a propósito: la comparten "Antecedentes generales" y "Descripción de
 * la propiedad", y lo único propio de cada una es QUÉ entradas y QUÉ pendientes
 * declara. Cómo se dibuja no.
 *
 * Son BOTONES y no enlaces `#ancla`: un enlace de hash cambia la url, y la
 * guardia de cambios sin guardar escucha esa navegación. Un botón que hace
 * scroll no toca el historial.
 */
export function IndiceLateral({
  titulo,
  etiquetaDeLaNavegacion,
  entradas,
  avance,
  pendientes,
  onIr,
}: IndiceLateralProps) {
  const { hechas, total, porcentaje } = avance;
  const sinPendientes = pendientes.length === 0;

  return (
    <aside className="flex flex-col gap-3 lg:sticky lg:top-5">
      <nav
        aria-label={etiquetaDeLaNavegacion}
        className="border-border bg-card overflow-hidden rounded-xl border-[1.5px]"
      >
        <div className="border-divisor border-b px-3.5 pt-3 pb-2.5">
          <div className="text-ink text-[0.78125rem] font-bold tracking-[-0.01em]">
            {titulo}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="bg-muted h-[5px] flex-1 overflow-hidden rounded-full">
              <span
                className="bg-primary block h-full transition-all"
                style={{ width: `${porcentaje}%` }}
              />
            </div>
            <span className="text-ink-muted flex-none font-mono text-[0.65625rem] font-bold">
              {hechas}/{total}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-px px-1.5 pt-[0.3125rem] pb-[0.4375rem]">
          {entradas.map(({ destino, n, etiqueta, completa, cuenta, nivel = 0 }) => (
            <button
              key={destino}
              type="button"
              onClick={() => onIr(destino)}
              className={cn(
                "hover:bg-muted/60 focus-visible:ring-ring/50 flex min-w-0 items-center gap-[0.5625rem] rounded-md px-2 py-[0.4375rem] text-left outline-none focus-visible:ring-[3px]",
                nivel === 1 && "py-[0.1875rem] pl-6",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "flex-none rounded-full border-[1.5px]",
                  nivel === 1 ? "size-1.5" : "size-2",
                  completa
                    ? "bg-success-dot border-success-dot"
                    : "border-ink-ghost border-dashed bg-transparent",
                )}
              />
              {n ? (
                <span className="text-ink-hint flex-none font-mono text-[0.59375rem] font-bold">
                  {n}
                </span>
              ) : null}
              <span
                className={cn(
                  "text-ink-body min-w-0 flex-1 truncate",
                  nivel === 1 ? "text-[0.6875rem]" : "text-xs",
                  completa ? "font-semibold" : "font-bold",
                )}
              >
                {etiqueta}
              </span>
              {cuenta ? (
                <span className="text-ink-muted flex-none font-mono text-[0.625rem] font-bold">
                  {cuenta}
                </span>
              ) : null}
              <span className="sr-only">{completa ? "completa" : "pendiente"}</span>
            </button>
          ))}
        </div>
      </nav>

      <div
        className={cn(
          "rounded-xl border-[1.5px] px-3.5 py-3",
          sinPendientes ? "border-success-soft bg-success-soft/50" : "border-warning-dot/30 bg-warning-soft",
        )}
      >
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={cn("size-2 flex-none rounded-full", sinPendientes ? "bg-success-dot" : "bg-warning-dot")}
          />
          <span
            className={cn(
              "text-xs font-bold",
              sinPendientes ? "text-success-soft-foreground" : "text-warning-soft-foreground",
            )}
          >
            {sinPendientes ? "Pestaña sin pendientes" : "Pendientes de esta pestaña"}
          </span>
        </div>

        <div className="mt-2 flex flex-col gap-1.5">
          {sinPendientes ? (
            <p className="text-ink-body text-[0.71875rem] font-semibold">
              Nada pendiente en esta pestaña.
            </p>
          ) : (
            pendientes.map(({ destino, texto }) => (
              <button
                key={texto}
                type="button"
                onClick={() => onIr(destino)}
                className="text-ink-body hover:text-ink flex items-start gap-[0.4375rem] text-left text-[0.71875rem] font-semibold text-pretty"
              >
                <span
                  aria-hidden="true"
                  className="bg-ink-ghost mt-[0.3125rem] size-1 flex-none rounded-full"
                />
                {texto}
              </button>
            ))
          )}
        </div>
      </div>
    </aside>
  );
}

interface IndiceDeLaPestanaProps {
  completas: Record<ClaveDeSeccion, boolean>;
  pendientes: Pendiente[];
  /** Lleva a la tarjeta de esa sección. Lo decide quien la dibuja: acá no hay DOM. */
  onIr: (seccion: ClaveDeSeccion) => void;
}

/** El índice de "Antecedentes generales": las nueve tarjetas y lo que falta. */
export function IndiceDeLaPestana({ completas, pendientes, onIr }: IndiceDeLaPestanaProps) {
  return (
    <IndiceLateral
      titulo="Antecedentes generales"
      etiquetaDeLaNavegacion="Secciones de antecedentes generales"
      entradas={SECCIONES_DE_ANTECEDENTES.map(({ clave, n, etiqueta }) => ({
        destino: clave,
        n,
        etiqueta,
        completa: completas[clave],
      }))}
      avance={avanceDeLaPestana(completas)}
      pendientes={pendientes.map(({ seccion, texto }) => ({ destino: seccion, texto }))}
      onIr={(destino) => onIr(destino as ClaveDeSeccion)}
    />
  );
}
